import React from 'react';
import { Button } from '../ui/Button';
import { BarChart3, Target, Users, PenTool, TrendingUp } from 'lucide-react';

interface WelcomeMessageProps {
  agentType: string;
  onQuestionSelect: (question: string) => void;
}

const agentInfo = {
  data: {
    name: 'Analytics Agent',
    icon: BarChart3,
    color: 'blue',
    description: 'I can help you analyze your Google Analytics data and provide insights.',
    sampleQuestions: [
      'How many users visited my website yesterday?',
      'What are my top traffic sources this month?',
      'Show me the conversion funnel analysis',
      'Compare this month vs last month performance'
    ]
  },
  marketing: {
    name: 'Marketing Advisor',
    icon: Target,
    color: 'green',
    description: 'I provide strategic marketing guidance and optimization recommendations.',
    sampleQuestions: [
      'How can I improve my conversion rate?',
      'What marketing channels should I focus on?',
      'Create a marketing strategy for Q2',
      'How to optimize my ad spend allocation?'
    ]
  },
  strategy: {
    name: 'Strategy Consultant',
    icon: TrendingUp,
    color: 'purple',
    description: 'I help develop comprehensive marketing strategies and growth plans.',
    sampleQuestions: [
      'Develop a growth strategy for my business',
      'Analyze my competitive landscape',
      'Plan market expansion strategy',
      'Create a strategic roadmap for 2024'
    ]
  },
  persona: {
    name: 'Persona Expert',
    icon: Users,
    color: 'pink',
    description: 'I specialize in user persona development and audience analysis.',
    sampleQuestions: [
      'Create user personas from my analytics data',
      'Analyze my audience segments',
      'Map the customer journey',
      'Optimize targeting for different personas'
    ]
  },
  content: {
    name: 'Content Specialist',
    icon: PenTool,
    color: 'orange',
    description: 'I help with content marketing strategy and creative ideas.',
    sampleQuestions: [
      'Create a content strategy for my blog',
      'Generate blog post ideas for my industry',
      'Optimize my content for SEO',
      'Plan a content calendar for next quarter'
    ]
  }
};

export function WelcomeMessage({ agentType, onQuestionSelect }: WelcomeMessageProps) {
  const agent = agentInfo[agentType as keyof typeof agentInfo] || agentInfo.data;
  const Icon = agent.icon;

  return (
    <div className="flex justify-start">
      <div className="w-full max-w-4xl rounded-xl sm:rounded-lg p-4 sm:p-6 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 ml-2 mr-4 sm:mr-12 shadow-sm">
        {/* Agent Header */}
        <div className="flex items-center space-x-3 mb-4 sm:mb-6">
          <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-lg bg-${agent.color}-100 dark:bg-${agent.color}-900/30 flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-6 h-6 sm:w-7 sm:h-7 text-${agent.color}-600 dark:text-${agent.color}-400`} />
          </div>
          <div className="min-w-0">
            <h3 className="font-semibold text-gray-900 dark:text-white text-lg sm:text-xl">{agent.name}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-300">Ready to help you</p>
          </div>
        </div>

        {/* Welcome Message */}
        <div className="mb-4 sm:mb-6">
          <p className="text-gray-700 dark:text-gray-300 mb-3 sm:mb-4 text-base sm:text-base leading-relaxed">
            👋 Hello! {agent.description}
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            You can ask me questions in natural language, and I'll analyze your data to provide insights and recommendations.
          </p>
        </div>

        {/* Sample Questions - Mobile Optimized */}
        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 sm:mb-4">
            Here are some questions you can ask:
          </p>
          <div className="space-y-2 sm:space-y-3">
            {agent.sampleQuestions.map((question, index) => (
              <Button
                key={index}
                variant="outline"
                onClick={() => onQuestionSelect(question)}
                className="w-full text-left justify-start p-3 sm:p-4 h-auto min-h-[52px] sm:min-h-[48px] rounded-lg sm:rounded-lg border-gray-200 dark:border-gray-600 hover:border-blue-300 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-200 group"
              >
                <div className="flex items-start space-x-3 w-full">
                  <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 group-hover:bg-blue-100 dark:group-hover:bg-blue-800 flex items-center justify-center flex-shrink-0 transition-colors duration-200">
                    <span className="text-xs font-semibold text-gray-600 dark:text-gray-300 group-hover:text-blue-600 dark:group-hover:text-blue-300">
                      {index + 1}
                    </span>
                  </div>
                  <span className="text-sm sm:text-sm text-gray-700 dark:text-gray-200 group-hover:text-blue-700 dark:group-hover:text-blue-200 leading-relaxed text-left">
                    {question}
                  </span>
                </div>
              </Button>
            ))}
          </div>
        </div>

        {/* Additional Help Text - Mobile */}
        <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
          <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
            💡 <strong>Tip:</strong> Be specific with your questions for better results. 
            <span className="hidden sm:inline"> For example, "Show me traffic from last month" instead of just "traffic".</span>
            <span className="sm:hidden"> Try asking about specific time periods or metrics.</span>
          </p>
        </div>
      </div>
    </div>
  );
}